import { supabase } from '@/shared/supabase/client'
import { isScreenshotFixtureMode } from '@/shared/lib/screenshotMode'
import {
  screenshotFixtureDocuments,
  screenshotFixtureTemplates,
} from '@/shared/screenshotSeed/synapScreenshotFixtures'
import type { StoredPrescriptionSignatureKind } from '@/entities/clinicalDocument/types'
import { AppError } from '@/shared/lib/appError'
import { toRepositoryError } from '@/shared/repositories/common/repositoryError'
import { normalizeDocumentDate } from '@/shared/lib/clinicalDocumentUtils'
import type { Database, Json } from '@/shared/supabase/database.types'

export interface ApiTemplate {
  id: string
  type: 'prescription' | 'certificate' | 'request' | 'generic'
  name: string
  description: string
  createdAt: string
  updatedAt: string
}

export interface ApiDocument {
  id: string
  patientId: string
  type: 'prescription' | 'certificate' | 'request' | 'generic'
  title: string
  summary: string
  content: string
  structuredFields?: Json | null
  details: string
  templateId?: string
  prescriptionSignatureKind?: StoredPrescriptionSignatureKind | null
  createdByUserId: string
  createdAt: string
  updatedAt: string
  documentDate: string
}

export interface CreateTemplatePayload {
  type?: 'prescription' | 'certificate' | 'request' | 'generic'
  name: string
  description: string
}

export interface UpdateTemplatePayload {
  name?: string
  description?: string
}

export interface CreateDocumentPayload {
  patientId: string
  type: 'prescription' | 'certificate' | 'request' | 'generic'
  title: string
  summary?: string | null
  content: string
  structuredFields?: Json | null
  templateId?: string
  prescriptionSignatureKind?: StoredPrescriptionSignatureKind | null
  documentDate?: string | null
}

export interface UpdateDocumentPayload {
  patientId?: string
  type?: 'prescription' | 'certificate' | 'request' | 'generic'
  title?: string
  summary?: string | null
  content?: string
  structuredFields?: Json | null
  templateId?: string
  prescriptionSignatureKind?: StoredPrescriptionSignatureKind | null
  documentDate?: string | null
}

type DocumentModelRow = Database['public']['Tables']['document_models']['Row']
type ClinicalDocumentRow = Database['public']['Tables']['clinical_documents']['Row']

const getCurrentUserId = async (): Promise<string> => {
  const { data, error } = await supabase.auth.getUser()
  if (error) {
    throw toRepositoryError(error, 'Não foi possível validar a sessão do usuário.', 401)
  }
  if (!data.user) {
    throw new AppError('Usuário não autenticado.', { status: 401, code: 'UNAUTHORIZED' })
  }
  return data.user.id
}

const assertCanMutateClinicalDocuments = async (): Promise<void> => {
  const { data, error } = await supabase.auth.getUser()
  if (error) {
    throw toRepositoryError(error, 'Não foi possível validar a sessão do usuário.', 401)
  }
  const role = (data.user?.user_metadata as Record<string, unknown> | undefined)?.role
  if (role === 'patient') {
    throw new AppError('Perfil paciente não pode criar ou editar documentos clínicos.', {
      status: 403,
      code: 'FORBIDDEN',
    })
  }
}

const toApiTemplate = (row: DocumentModelRow): ApiTemplate => ({
  id: row.id,
  type: row.type,
  name: row.name,
  description: row.description,
  createdAt: row.created_at,
  updatedAt: row.updated_at,
})

const RX_KIND_SET = new Set<string>([
  'prescription_simple',
  'prescription_controlled',
  'prescription_antimicrobial',
])

const prescriptionKindFromRow = (
  value: ClinicalDocumentRow['prescription_signature_kind'],
): StoredPrescriptionSignatureKind | undefined => {
  if (!value || !RX_KIND_SET.has(value)) return undefined
  return value as StoredPrescriptionSignatureKind
}

const summaryForApi = (row: ClinicalDocumentRow): string => {
  if (row.summary == null) return ''
  const t = String(row.summary).trim()
  return t
}

const toApiDocument = (row: ClinicalDocumentRow): ApiDocument => {
  const pk = prescriptionKindFromRow(row.prescription_signature_kind)
  return {
    id: row.id,
    patientId: row.patient_id,
    type: row.type,
    title: row.title,
    summary: summaryForApi(row),
    content: row.content,
    structuredFields: row.structured_fields ?? null,
    details: row.content,
    templateId: row.template_id ?? undefined,
    prescriptionSignatureKind: pk ?? null,
    createdByUserId: row.created_by_user_id,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
    documentDate: normalizeDocumentDate(row.document_date ?? row.created_at),
  }
}

const summaryForDb = (summary: string | null | undefined): string | null => {
  if (summary === undefined || summary === null) return null
  const t = summary.trim()
  return t.length ? t : null
}

export const clinicalDocumentsRepository = {
  listTemplates: async (): Promise<{ templates: ApiTemplate[] }> => {
    if (isScreenshotFixtureMode()) {
      return { templates: screenshotFixtureTemplates() }
    }
    try {
      await getCurrentUserId()
      const { data, error } = await supabase
        .from('document_models')
        .select('*')
        .order('updated_at', { ascending: false })

      if (error) {
        throw toRepositoryError(error, 'Não foi possível listar modelos.')
      }

      return { templates: (data ?? []).map(toApiTemplate) }
    } catch (error) {
      throw toRepositoryError(error, 'Não foi possível listar modelos.')
    }
  },

  /**
   * Importação em lote de modelos de documento (mesmo utilizador autenticado).
   * Ignora entradas sem nome ou sem descrição/conteúdo.
   */
  createTemplatesMany: async (
    items: CreateTemplatePayload[],
  ): Promise<{ created: number; templates: ApiTemplate[] }> => {
    try {
      const ownerUserId = await getCurrentUserId()
      const rows = items
        .map((item) => {
          const name = item.name?.trim()
          const description = item.description?.trim()
          if (!name || !description) return null
          return {
            owner_user_id: ownerUserId,
            type: (item.type ?? 'prescription') as DocumentModelRow['type'],
            name,
            description,
          }
        })
        .filter((r): r is NonNullable<typeof r> => r !== null)

      if (rows.length === 0) {
        return { created: 0, templates: [] }
      }

      const { data, error } = await supabase.from('document_models').insert(rows).select('*')

      if (error) {
        throw toRepositoryError(error, 'Não foi possível importar modelos de documento.')
      }

      return {
        created: data?.length ?? 0,
        templates: (data ?? []).map(toApiTemplate),
      }
    } catch (error) {
      throw toRepositoryError(error, 'Não foi possível importar modelos de documento.')
    }
  },

  createTemplate: async (
    payload: CreateTemplatePayload,
  ): Promise<{ template: ApiTemplate }> => {
    try {
      const ownerUserId = await getCurrentUserId()
      const { data, error } = await supabase
        .from('document_models')
        .insert({
          owner_user_id: ownerUserId,
          type: payload.type ?? 'prescription',
          name: payload.name.trim(),
          description: payload.description.trim(),
        })
        .select('*')
        .single()

      if (error) {
        throw toRepositoryError(error, 'Não foi possível criar modelo.')
      }

      return { template: toApiTemplate(data) }
    } catch (error) {
      throw toRepositoryError(error, 'Não foi possível criar modelo.')
    }
  },

  updateTemplate: async (
    templateId: string,
    payload: UpdateTemplatePayload,
  ): Promise<{ template: ApiTemplate }> => {
    try {
      const { data, error } = await supabase
        .from('document_models')
        .update({
          name: payload.name?.trim(),
          description: payload.description?.trim(),
        })
        .eq('id', templateId)
        .select('*')
        .single()

      if (error) {
        throw toRepositoryError(error, 'Não foi possível atualizar modelo.')
      }

      return { template: toApiTemplate(data) }
    } catch (error) {
      throw toRepositoryError(error, 'Não foi possível atualizar modelo.')
    }
  },

  removeTemplate: async (templateId: string): Promise<{ ok: boolean }> => {
    try {
      const { error } = await supabase.from('document_models').delete().eq('id', templateId)
      if (error) {
        throw toRepositoryError(error, 'Não foi possível remover modelo.')
      }
      return { ok: true }
    } catch (error) {
      throw toRepositoryError(error, 'Não foi possível remover modelo.')
    }
  },

  listDocuments: async (patientId?: string): Promise<{ documents: ApiDocument[] }> => {
    if (isScreenshotFixtureMode()) {
      const all = screenshotFixtureDocuments()
      const documents = patientId ? all.filter((d) => d.patientId === patientId) : all
      return { documents }
    }
    try {
      await getCurrentUserId()
      let query = supabase
        .from('clinical_documents')
        .select('*')
        .order('created_at', { ascending: false })
      if (patientId) {
        query = query.eq('patient_id', patientId)
      }

      const { data, error } = await query
      if (error) {
        throw toRepositoryError(error, 'Não foi possível listar documentos.')
      }

      return { documents: (data ?? []).map(toApiDocument) }
    } catch (error) {
      throw toRepositoryError(error, 'Não foi possível listar documentos.')
    }
  },

  /**
   * Histórico paginado (50/50). O período vai na query (`created_at`) para não
   * paginar a tabela inteira quando o filtro local esconde as linhas. Busca
   * opcional em título/resumo/conteúdo (remédio, CID) e, com `searchPatientIds`, também nos
   * documentos desses pacientes (DOCL-01: «Paciente, remédio ou CID» — o nome do paciente não
   * está em `clinical_documents`, então quem chama resolve os ids pelo nome).
   */
  listDocumentsPage: async (params: {
    limit?: number
    offset?: number
    search?: string
    /** ISO inclusivo. */
    createdFrom?: string
    /** ISO inclusivo. */
    createdTo?: string
    /** Pacientes cujo nome casa com `search` (OR com o texto). Ignorado sem `search`. */
    searchPatientIds?: string[]
  } = {}): Promise<{ documents: ApiDocument[]; hasMore: boolean }> => {
    const limit = Math.min(Math.max(params.limit ?? 50, 1), 100)
    const offset = Math.max(params.offset ?? 0, 0)
    const search = params.search?.trim() ?? ''
    const createdFrom = params.createdFrom?.trim() ?? ''
    const createdTo = params.createdTo?.trim() ?? ''
    const searchPatientIds = search
      ? [...new Set((params.searchPatientIds ?? []).filter((id) => /^[0-9a-zA-Z-]+$/.test(id)))].slice(0, 50)
      : []

    if (isScreenshotFixtureMode()) {
      let all = screenshotFixtureDocuments()
      if (createdFrom) all = all.filter((d) => d.createdAt >= createdFrom)
      if (createdTo) all = all.filter((d) => d.createdAt <= createdTo)
      if (search) {
        const q = search.toLowerCase()
        all = all.filter(
          (d) =>
            d.title.toLowerCase().includes(q) ||
            d.summary.toLowerCase().includes(q) ||
            d.content.toLowerCase().includes(q) ||
            searchPatientIds.includes(d.patientId),
        )
      }
      const slice = all.slice(offset, offset + limit)
      return { documents: slice, hasMore: offset + slice.length < all.length }
    }

    try {
      let query = supabase
        .from('clinical_documents')
        .select(
          'id, patient_id, type, title, summary, content, structured_fields, template_id, prescription_signature_kind, created_by_user_id, created_at, updated_at, document_date',
        )
        .order('created_at', { ascending: false })
        .range(offset, offset + limit - 1)

      if (createdFrom) query = query.gte('created_at', createdFrom)
      if (createdTo) query = query.lte('created_at', createdTo)

      if (search) {
        const escaped = search.replace(/\\/g, '\\\\').replace(/%/g, '\\%').replace(/_/g, '\\_').replace(/"/g, '')
        const pattern = `%${escaped}%`
        const patientFilter = searchPatientIds.length ? `,patient_id.in.(${searchPatientIds.join(',')})` : ''
        query = query.or(
          `title.ilike."${pattern}",summary.ilike."${pattern}",content.ilike."${pattern}"${patientFilter}`,
        )
      }

      const { data, error } = await query
      if (error) {
        throw toRepositoryError(error, 'Não foi possível listar documentos.')
      }

      const documents = (data ?? []).map(toApiDocument)
      return { documents, hasMore: documents.length === limit }
    } catch (error) {
      throw toRepositoryError(error, 'Não foi possível listar documentos.')
    }
  },

  createDocument: async (
    payload: CreateDocumentPayload,
  ): Promise<{ document: ApiDocument }> => {
    try {
      await assertCanMutateClinicalDocuments()
      const userId = await getCurrentUserId()
      const cleanTitle = payload.title.trim()
      const cleanContent = payload.content.trim()
      const summaryDb = summaryForDb(payload.summary !== undefined ? payload.summary : '')
      const rxKind: ClinicalDocumentRow['prescription_signature_kind'] =
        payload.type === 'prescription'
          ? (payload.prescriptionSignatureKind ?? 'prescription_simple')
          : null

      const { data, error } = await supabase
        .from('clinical_documents')
        .insert({
          patient_id: payload.patientId,
          created_by_user_id: userId,
          type: payload.type,
          title: cleanTitle,
          summary: summaryDb,
          content: cleanContent,
          structured_fields: payload.structuredFields ?? null,
          template_id: payload.templateId ?? null,
          prescription_signature_kind: rxKind,
          document_date: payload.documentDate
            ? normalizeDocumentDate(payload.documentDate)
            : undefined,
        })
        .select('*')
        .single()

      if (error) {
        throw toRepositoryError(error, 'Não foi possível criar documento clínico.')
      }

      return { document: toApiDocument(data) }
    } catch (error) {
      throw toRepositoryError(error, 'Não foi possível criar documento clínico.')
    }
  },

  updateDocument: async (
    documentId: string,
    payload: UpdateDocumentPayload,
  ): Promise<{ document: ApiDocument }> => {
    try {
      await assertCanMutateClinicalDocuments()
      const updateData: Database['public']['Tables']['clinical_documents']['Update'] = {}

      if (payload.patientId !== undefined) updateData.patient_id = payload.patientId
      if (payload.type !== undefined) {
        updateData.type = payload.type
        if (payload.type !== 'prescription') {
          updateData.prescription_signature_kind = null
        }
      }
      if (payload.title !== undefined) updateData.title = payload.title.trim()
      if (payload.summary !== undefined) updateData.summary = summaryForDb(payload.summary)
      if (payload.content !== undefined) updateData.content = payload.content.trim()
      if (payload.structuredFields !== undefined) updateData.structured_fields = payload.structuredFields
      if (payload.templateId !== undefined) {
        updateData.template_id = payload.templateId ?? null
      }
      if (payload.prescriptionSignatureKind !== undefined) {
        updateData.prescription_signature_kind = payload.prescriptionSignatureKind
      }
      if (payload.documentDate !== undefined) {
        updateData.document_date = payload.documentDate
          ? normalizeDocumentDate(payload.documentDate)
          : undefined
      }

      const { data, error } = await supabase
        .from('clinical_documents')
        .update(updateData)
        .eq('id', documentId)
        .select('*')
        .single()

      if (error) {
        throw toRepositoryError(error, 'Não foi possível atualizar documento clínico.')
      }

      return { document: toApiDocument(data) }
    } catch (error) {
      throw toRepositoryError(error, 'Não foi possível atualizar documento clínico.')
    }
  },

  removeDocument: async (documentId: string): Promise<{ ok: boolean }> => {
    try {
      await assertCanMutateClinicalDocuments()
      const { error } = await supabase.from('clinical_documents').delete().eq('id', documentId)
      if (error) {
        throw toRepositoryError(error, 'Não foi possível remover documento clínico.')
      }
      return { ok: true }
    } catch (error) {
      throw toRepositoryError(error, 'Não foi possível remover documento clínico.')
    }
  },

  /**
   * Copia um documento assinado como novo rascunho editável.
   * Não copia a assinatura nem o signed_document_id — o novo documento começa limpo.
   */
  copyAsDraft: async (sourceId: string): Promise<{ document: ApiDocument }> => {
    try {
      const userId = await getCurrentUserId()

      const { data: original, error: fetchErr } = await supabase
        .from('clinical_documents')
        .select('*')
        .eq('id', sourceId)
        .single()
      if (fetchErr || !original) {
        throw new Error('Documento original não encontrado')
      }

      const { data: newDoc, error: insertErr } = await supabase
        .from('clinical_documents')
        .insert({
          patient_id: original.patient_id,
          created_by_user_id: userId,
          type: original.type,
          title: original.title,
          summary: original.summary ?? null,
          content: original.content,
          structured_fields: original.structured_fields ?? null,
          template_id: original.template_id ?? null,
          prescription_signature_kind: original.prescription_signature_kind ?? null,
        })
        .select('*')
        .single()

      if (insertErr || !newDoc) {
        throw new Error('Falha ao criar cópia em rascunho')
      }

      return { document: toApiDocument(newDoc) }
    } catch (error) {
      throw toRepositoryError(error, 'Não foi possível copiar o documento como rascunho.')
    }
  },
}
